/** 多策略对比页（F-020 + F-023）。参考 v0 设计布局。 */
import { BarChart3, Trash2, Download, AlertTriangle } from 'lucide-react';
import { useResults } from '../results-store';
import { RadarChart } from '../components/RadarChart';
import { generateReport } from '../../data/report';
import { downloadText } from '../../data/download';
import { strategyLabel } from '../../strategy/registry';
import { sceneLabel } from '../../data/templates';
import { Section } from '../primitives';
import { cn } from '../cn';
import type { ExperimentResult } from '../../data/export-csv';

const COLS: { key: string; label: string; get: (r: ExperimentResult) => number; fmt: (v: number) => string; better: 'min' | 'max' }[] = [
  { key: 'avgWait', label: '平均等待(s)', get: (r) => r.summary.avgWaitMs, fmt: (v) => (v / 1000).toFixed(1), better: 'min' },
  { key: 'maxWait', label: '最长等待(s)', get: (r) => r.summary.maxWaitMs, fmt: (v) => (v / 1000).toFixed(1), better: 'min' },
  { key: 'avgRide', label: '平均乘梯(s)', get: (r) => r.summary.avgRideMs, fmt: (v) => (v / 1000).toFixed(1), better: 'min' },
  { key: 'throughput', label: '吞吐(/h)', get: (r) => r.summary.throughputPerHour, fmt: (v) => v.toFixed(0), better: 'max' },
  { key: 'utilization', label: '利用率', get: (r) => r.summary.carUtilization, fmt: (v) => `${(v * 100).toFixed(0)}%`, better: 'max' },
  { key: 'energy', label: '能耗(kWh)', get: (r) => r.summary.energyKwh, fmt: (v) => v.toFixed(2), better: 'min' },
];

export function ComparePage() {
  const { compare, removeFromCompare, clearCompare } = useResults();
  const groups = new Set(compare.map((r) => `${sceneLabel(r.sceneId)}/种子${r.seed}`));
  const comparable = groups.size <= 1;

  const best = (col: (typeof COLS)[number]): string | undefined => {
    const ok = compare.filter((r) => r.ok);
    if (ok.length < 2) return undefined;
    const vals = ok.map((r) => col.get(r));
    const t = col.better === 'min' ? Math.min(...vals) : Math.max(...vals);
    return ok.find((r) => col.get(r) === t)?.id;
  };

  if (compare.length === 0) {
    return (
      <div className="max-w-5xl mx-auto">
        <Section title="多策略对比（F-020）" icon={BarChart3}>
          <p className="text-sm text-muted-foreground">尚无对比项。先在「批量实验」运行并把结果加入对比。</p>
        </Section>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <h1 className="text-base font-semibold text-foreground flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" />多策略对比</h1>
        <div className="ml-auto flex gap-3">
          <button onClick={() => downloadText('report.md', generateReport(compare), 'text/markdown')} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80"><Download className="w-3.5 h-3.5" />生成报告（F-023）</button>
          <button onClick={clearCompare} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><Trash2 className="w-3.5 h-3.5" />清空</button>
        </div>
      </div>

      {!comparable && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-accent/30 bg-accent/5 text-sm text-accent">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          实验条件不一致（{[...groups].join('、')}），对比仅供参考（AC-F020-02）。建议同场景同种子对比。
        </div>
      )}

      <Section title="指标对比（绿=最优）">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-[11px] font-mono uppercase tracking-wider">
              <tr className="border-b border-border">
                <th className="text-left py-1.5 px-2">实验</th>
                {COLS.map((c) => <th key={c.key} className="text-right py-1.5 px-2">{c.label}</th>)}
                <th />
              </tr>
            </thead>
            <tbody className="text-foreground num">
              {compare.map((r) => (
                <tr key={r.id} className="border-b border-border/50">
                  <td className="py-1.5 px-2">
                    <div className="text-foreground">{strategyLabel(r.strategyId)}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{sceneLabel(r.sceneId)}/种子{r.seed}</div>
                  </td>
                  {COLS.map((c) => {
                    const win = best(c);
                    return <td key={c.key} className={cn('text-right py-1.5 px-2', win === r.id && 'text-chart-3 font-semibold')}>{r.ok ? c.fmt(c.get(r)) : '—'}</td>;
                  })}
                  <td className="text-right py-1.5 px-2"><button onClick={() => removeFromCompare(r.id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-3 h-3" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="雷达图">
        {compare.filter((r) => r.ok).length >= 2 ? <RadarChart results={compare.filter((r) => r.ok)} /> : <p className="text-xs text-muted-foreground">需至少 2 个有效结果才能绘制有意义的雷达对比。</p>}
      </Section>
    </div>
  );
}
